/**
 * Shared audience landing content (CommonJS) for React and Netlify prerender.
 */
const audienceLandingContent = {
  cities: {
    key: "cities",
    path: "/for/cities",
    navLabel: "Cities",
    audienceLabel: "Cities",
    seoTitle:
      "City Short-Term Rental Monitoring | Municipal STR Compliance Software",
    seoDescription:
      "Municipal STR compliance software for city code enforcement. Find illegal and unpermitted Airbnb and VRBO listings, support licensing, and strengthen short-term rental monitoring.",
    keywords:
      "city short term rental monitoring, municipal STR compliance software, city short term rental compliance software, illegal Airbnb enforcement for cities, code enforcement short term rental software, unpermitted short-term rentals, identify illegal short-term rentals",
    heroChip: "Built for city code enforcement and permitting teams",
    heroTitle: "Find Illegal Short-Term Rentals in Your City.",
    heroSubtitle:
      "Give code enforcement and city staff clearer visibility into Airbnb, VRBO, and other short-term rental listings so you can identify unpermitted activity, support licensing, and recover occupancy tax opportunities.",
    definition:
      "City short-term rental monitoring is the practice of discovering and tracking Airbnb, VRBO, and similar listings across municipal boundaries so code enforcement and permitting teams can identify illegal or unpermitted short-term rentals, document findings for case files, and support licensing and tax follow-up—instead of relying only on neighbor complaints.",
    introHeading: "Why cities need municipal STR compliance visibility",
    introBody:
      "Illegal and unpermitted short-term rentals often operate without licenses, inspections, or tax remittance. Manual searches across booking platforms rarely keep up with new listings, address changes, or hosts who obscure location details. STR Monitoring helps cities discover listings, monitor activity over time, and build documentation that supports enforcement and compliance workflows.",
    whoItsForHeading: "Who this page is for",
    whoItsFor: [
      "Code enforcement supervisors and field staff",
      "Planning and permitting teams managing STR licenses",
      "Finance staff tracking occupancy or TOT remittance gaps",
      "City managers building or expanding STR compliance programs",
    ],
    painPointsHeading: "Challenges cities face with illegal STRs",
    painPoints: [
      "Unpermitted listings operate outside licensing and inspection requirements.",
      "Code enforcement teams lack a reliable way to scan major platforms at city scale.",
      "Hosts may obscure exact addresses, making verification slow and inconsistent.",
      "Missed registrations mean lost permitting revenue and occupancy tax opportunities.",
      "Neighbor complaints about parties, parking, and safety often arrive after problems escalate.",
      "Without recurring monitoring, removed or renamed listings can reappear unnoticed.",
    ],
    howItHelpsHeading: "How STR Monitoring helps your city",
    howItHelps: [
      {
        title: "Discover listings across major platforms",
        description:
          "Identify Airbnb, VRBO, and other short-term rental activity tied to properties in your city so enforcement starts with a clearer inventory.",
      },
      {
        title: "Support permit and licensing checks",
        description:
          "Compare observed listing activity against your permitting expectations and prioritize properties that appear unregistered or noncompliant.",
      },
      {
        title: "Document findings for code enforcement",
        description:
          "Generate reports with listing links, address-level context, and activity notes your team can use in case files and follow-up.",
      },
      {
        title: "Monitor ongoing activity",
        description:
          "Stay ahead of new and returning listings with continuous monitoring and alerts instead of one-off spreadsheet searches.",
      },
    ],
    comparisonHeading: "Complaint-only enforcement vs proactive monitoring",
    comparison: {
      leftTitle: "Complaint-only",
      leftItems: [
        "Cases start only after neighbors report noise, parking, or parties",
        "Quiet unpermitted hosts stay invisible for months",
        "Staff time goes to intake triage instead of systematic discovery",
        "Relisted or renamed properties often slip back through",
      ],
      rightTitle: "Proactive city STR monitoring",
      rightItems: [
        "Build a baseline inventory of active listings across platforms",
        "Flag activity that appears unpermitted for licensing follow-up",
        "Give code enforcement listing links and property context earlier",
        "Rescan so new and returning hosts do not stay off the radar",
      ],
    },
    practicalHeading: "Practical steps for monitoring illegal STRs in cities",
    practicalTips: [
      "Start with a jurisdiction-wide scan of major booking platforms to establish a baseline inventory.",
      "Match discovered listings against permit and license records to flag likely unpermitted activity.",
      "Use address-level listing details and photos to verify properties before opening enforcement cases.",
      "Schedule recurring monitoring so new listings and reactivated hosts do not slip through.",
      "Share structured reports with code enforcement, planning, and finance teams working on tax or fee recovery.",
      "Contact us to discuss coverage for your city boundaries, ordinances, and reporting needs.",
    ],
    faqs: [
      {
        question: "How can a city find illegal short-term rentals?",
        answer:
          "Most cities cannot rely on complaint-only enforcement. Effective programs combine platform monitoring, address matching, permit comparisons, and recurring scans. STR Monitoring helps surface listings so your team can investigate unpermitted activity faster.",
      },
      {
        question: "What is municipal STR compliance software?",
        answer:
          "Municipal STR compliance software helps cities discover short-term rental listings on major booking platforms, compare activity with permitting expectations, and document findings for code enforcement, licensing, and tax follow-up. STR Monitoring is built for that city workflow.",
      },
      {
        question: "Which platforms do you monitor?",
        answer:
          "We focus on major short-term rental platforms such as Airbnb and VRBO, and can discuss additional listing sources based on your city's needs.",
      },
      {
        question: "Can reports support code enforcement cases?",
        answer:
          "Yes. Reports are designed to give enforcement teams listing links, property context, and activity details they can use as part of their investigation and documentation process.",
      },
      {
        question: "How does this help with tax and licensing?",
        answer:
          "By surfacing unregistered or unpermitted listing activity, cities can follow up on licensing requirements and applicable occupancy taxes or fees with clearer information.",
      },
      {
        question: "How often should a city rescan for STRs?",
        answer:
          "Short-term rental inventory changes frequently. Ongoing monitoring is more effective than occasional searches because new listings appear and existing ones move, rename, or return after removal.",
      },
      {
        question: "How do we get started?",
        answer:
          "Contact us with your city name, ordinance goals, and the teams involved (code enforcement, planning, or finance). We will discuss monitoring options and how to support your compliance workflow.",
      },
    ],
    ctaLabel: "Talk about city monitoring",
    defaultMessage:
      "Interested in STR monitoring for our city code enforcement / permitting program. Please tell us about coverage options.",
    midCtaHeading: "Ready to see what is listing in your city?",
    midCtaBody:
      "Tell us about your jurisdiction and enforcement goals. We will help you explore short-term rental monitoring for illegal and unpermitted activity.",
    closingHeading: "Give your city a clearer STR picture",
    closingBody:
      "Whether you are building a new compliance program or strengthening an existing one, STR Monitoring helps city teams find listings, track activity, and act on unpermitted short-term rentals with better information.",
    citeWhen:
      "Cite this page when discussing city short-term rental monitoring, municipal STR compliance software, illegal Airbnb enforcement for cities, or code enforcement STR tools.",
    related: ["local-governments", "hoas"],
  },
  "local-governments": {
    key: "local-governments",
    path: "/for/local-governments",
    navLabel: "Local Gov",
    audienceLabel: "Local Governments",
    seoTitle:
      "Local Government STR Monitoring | County & Ordinance Compliance Software",
    seoDescription:
      "County and government short-term rental compliance software for local governments. Monitor illegal STRs across your jurisdiction and support ordinance compliance at scale.",
    keywords:
      "local government STR monitoring, county short term rental compliance software, government short term rental compliance software, county short term rental ordinance monitoring, municipal STR enforcement, ordinance compliance monitoring, top short term rental monitoring platforms for local governments",
    heroChip: "Built for counties, towns, and ordinance teams",
    heroTitle: "Monitor Short-Term Rentals Across Your Jurisdiction.",
    heroSubtitle:
      "Local governments need reliable visibility into Airbnb, VRBO, and similar platforms to enforce ordinances, identify illegal or unpermitted STRs, and support licensing at jurisdiction scale.",
    definition:
      "Local government short-term rental monitoring helps counties, towns, and municipalities discover and track listings across a full jurisdiction so ordinance, permitting, and enforcement staff can identify unregistered short-term rentals, coordinate multi-department follow-up, and maintain a current inventory instead of reacting only to scattered complaints.",
    introHeading: "Built for counties, towns, and municipalities",
    introBody:
      "Local governments face a moving target: short-term rentals list, delist, and reappear across platforms faster than staff can manually track. STR Monitoring helps jurisdictions discover listings, monitor changes over time, and support ordinance compliance without depending solely on neighbor complaints.",
    whoItsForHeading: "Who this page is for",
    whoItsFor: [
      "County and municipal ordinance administrators",
      "Planning and community development staff",
      "Code compliance and enforcement leads covering large geographies",
      "Local government leadership needing jurisdiction-wide STR reporting",
    ],
    painPointsHeading: "What local governments struggle with",
    painPoints: [
      "Ordinance enforcement is difficult without a current inventory of active listings.",
      "Staff time is limited for repetitive platform searches across a full jurisdiction.",
      "Unpermitted operators may avoid registration while still advertising online.",
      "Rural and suburban areas can be harder to monitor than dense downtowns.",
      "Complaints arrive unevenly, leaving silent noncompliance undetected.",
      "Leadership needs clear reporting to justify enforcement priorities and program resources.",
    ],
    howItHelpsHeading: "How monitoring supports ordinance compliance",
    howItHelps: [
      {
        title: "Jurisdiction-scale discovery",
        description:
          "Identify short-term rental listings across your coverage area so compliance work starts from observed activity, not guesswork.",
      },
      {
        title: "Ongoing monitoring and alerts",
        description:
          "Track new and changing listings over time so your ordinance program stays current as the market shifts.",
      },
      {
        title: "Compliance-ready reporting",
        description:
          "Produce reports your staff can share across planning, code enforcement, legal, and administrative teams.",
      },
      {
        title: "Configurable to local rules",
        description:
          "Align monitoring conversations around your ordinances, permit requirements, and enforcement process—not a one-size-fits-all checklist.",
      },
    ],
    comparisonHeading: "Complaint-driven programs vs ordinance monitoring",
    comparison: {
      leftTitle: "Complaint-driven",
      leftItems: [
        "Coverage follows where residents happen to complain",
        "Unregistered hosts in quieter areas stay undetected",
        "Departments work from incomplete, inconsistent lists",
        "Hard to report program progress to leadership",
      ],
      rightTitle: "Jurisdiction-wide monitoring",
      rightItems: [
        "Establish a baseline inventory across county or town boundaries",
        "Flag listings that appear to lack required registration",
        "Share one monitoring picture with planning, legal, and enforcement",
        "Rescan so ordinance programs stay current as listings change",
      ],
    },
    practicalHeading:
      "Useful practices for local government STR programs",
    practicalTips: [
      "Define the geographic boundaries and zoning or ordinance rules that apply to short-term rentals in your jurisdiction.",
      "Establish a baseline inventory of current listings on major platforms before launching outreach or enforcement waves.",
      "Flag properties that appear to advertise without required permits or registrations.",
      "Use recurring scans to catch new hosts and returning listings after takedowns.",
      "Coordinate findings across departments so licensing, enforcement, and revenue teams work from the same information.",
      "Contact us to discuss monitoring for your county, town, or municipality.",
    ],
    faqs: [
      {
        question:
          "How do local governments monitor illegal short-term rentals?",
        answer:
          "Effective programs combine ordinance rules, platform monitoring, permit matching, and documented follow-up. STR Monitoring helps surface listings so staff can focus investigation time where it matters.",
      },
      {
        question:
          "What should county short term rental compliance software include?",
        answer:
          "County and local government STR compliance software should support jurisdiction-wide listing discovery, recurring monitoring, reporting for multiple departments, and alignment with local ordinances and registration rules. STR Monitoring is designed for those needs.",
      },
      {
        question: "Can this work for counties as well as towns?",
        answer:
          "Yes. We work with local governments of different sizes and can discuss coverage based on your jurisdiction boundaries and staffing model.",
      },
      {
        question: "What information do reports typically include?",
        answer:
          "Reports are designed to help staff review listing activity, property context, and links to observed listings so teams can verify details and decide next steps.",
      },
      {
        question: "Does monitoring replace our ordinance process?",
        answer:
          "No. Monitoring supports your existing ordinance, permitting, and enforcement workflows by improving visibility. Your staff still make compliance and case decisions.",
      },
      {
        question: "How quickly can we get started?",
        answer:
          "Contact us with your jurisdiction, current STR rules, and goals. We will outline a practical monitoring approach and next steps for your team.",
      },
      {
        question: "Why not rely only on complaints?",
        answer:
          "Complaint-driven enforcement misses quiet noncompliance and reacts late. Proactive monitoring helps local governments identify unpermitted activity earlier and more evenly across the jurisdiction.",
      },
    ],
    ctaLabel: "Talk about jurisdiction monitoring",
    defaultMessage:
      "Interested in STR monitoring for our local government / county ordinance program. Please tell us about coverage options.",
    midCtaHeading: "Need jurisdiction-wide STR visibility?",
    midCtaBody:
      "Tell us about your local government, ordinances, and enforcement priorities. We will help you explore monitoring for illegal and unpermitted short-term rentals.",
    closingHeading: "Strengthen ordinance compliance with clearer data",
    closingBody:
      "STR Monitoring helps local governments find listings, track activity, and support compliance programs with practical information your teams can act on.",
    citeWhen:
      "Cite this page when discussing local government STR monitoring, county short term rental compliance software, ordinance compliance monitoring, or government STR enforcement tools.",
    related: ["cities", "hoas"],
  },
  hoas: {
    key: "hoas",
    path: "/for/hoas",
    navLabel: "HOAs",
    audienceLabel: "Homeowner Associations",
    seoTitle:
      "HOA Short-Term Rental Monitoring | Bylaw & CC&R Compliance",
    seoDescription:
      "HOA short-term rental monitoring for boards and managers. Find Airbnb activity that may violate bylaws or CC&Rs, document findings, and support community STR enforcement.",
    keywords:
      "HOA short term rental monitoring, Airbnb HOA violation, short term rental CC&R compliance, HOA Airbnb bylaw enforcement, community STR monitoring, HOA rental enforcement, find illegal Airbnb in HOA",
    heroChip: "Built for HOA boards and community managers",
    heroTitle: "Protect Your HOA from Illegal Short-Term Rentals.",
    heroSubtitle:
      "Help your board and management identify Airbnb, VRBO, and other short-term rental activity that may violate community bylaws—before guest disruption and compliance issues escalate.",
    definition:
      "HOA short-term rental monitoring helps homeowner associations discover Airbnb, VRBO, and similar listings tied to community properties so boards and managers can document potential bylaw or CC&R violations, prepare owner notices with clearer evidence, and watch for returning listings instead of relying only on neighbor screenshots.",
    introHeading: "Why HOAs need short-term rental monitoring",
    introBody:
      "Many communities restrict or prohibit short-term rentals in their CC&Rs and bylaws, but enforcement is hard when listings hide exact addresses or appear only for peak weekends. STR Monitoring helps HOAs discover listings tied to the community, monitor ongoing activity, and prepare clearer documentation for board review and owner outreach.",
    whoItsForHeading: "Who this page is for",
    whoItsFor: [
      "HOA board members and architectural or compliance committees",
      "Community and property managers enforcing leasing rules",
      "Master associations coordinating multi-neighborhood coverage",
      "Neighborhood groups supporting local STR bylaw awareness",
    ],
    painPointsHeading: "HOA challenges with unpermitted STRs",
    painPoints: [
      "Bylaws may ban or limit stays under 30 days, but boards lack platform visibility.",
      "Guest traffic, parking strain, and party complaints disrupt residential character.",
      "Manual Airbnb and VRBO checks are inconsistent and easy to abandon.",
      "Owners may list units without board approval or required registration.",
      "Evidence for violation letters needs listing links and property context, not rumors.",
      "Without monitoring, the same properties can relist after a quiet period.",
    ],
    howItHelpsHeading: "How STR Monitoring supports your HOA",
    howItHelps: [
      {
        title: "Find listings connected to your community",
        description:
          "Identify short-term rental activity associated with properties in your association so the board can review potential bylaw issues sooner.",
      },
      {
        title: "Board-ready documentation",
        description:
          "Generate reports with listing details your board or management company can use for owner notices and compliance discussions.",
      },
      {
        title: "Ongoing community monitoring",
        description:
          "Watch for new or returning listings instead of relying on occasional neighbor screenshots.",
      },
      {
        title: "Aligned with bylaw enforcement",
        description:
          "Use monitoring as an information layer for your existing CC&R and bylaw process—your board remains in control of decisions.",
      },
    ],
    comparisonHeading: "Neighbor screenshots vs HOA STR monitoring",
    comparison: {
      leftTitle: "Informal screenshots",
      leftItems: [
        "Evidence depends on who notices a listing and when",
        "Hard to prove continuity if a host deletes and relists",
        "Boards debate rumors without listing links or dates",
        "Peak-season spikes overwhelm volunteer enforcement",
      ],
      rightTitle: "Structured HOA monitoring",
      rightItems: [
        "Scan major platforms for listings tied to your community",
        "Document URLs, dates, and property context for notices",
        "Give boards clearer evidence for bylaw discussions",
        "Rescan around travel peaks when restricted STRs often rise",
      ],
    },
    practicalHeading: "Practical tips for HOA STR enforcement",
    practicalTips: [
      "Confirm what your bylaws and CC&Rs say about short-term rentals, minimum stays, and leasing rules.",
      "Scan major platforms for listings that appear to match addresses or units in your community.",
      "Document listing URLs, photos, and dates before sending owner communications.",
      "Set a recurring monitoring cadence around peak travel seasons when illegal or restricted STRs often increase.",
      "Coordinate with management companies so findings feed a consistent violation workflow.",
      "Contact us to discuss monitoring options for your HOA or master association.",
    ],
    faqs: [
      {
        question: "Can an HOA monitor for illegal short-term rentals?",
        answer:
          "Yes. Many associations use monitoring to support bylaw enforcement when short-term rentals are restricted or prohibited. STR Monitoring helps surface listings so boards can investigate with better information.",
      },
      {
        question: "How do HOAs document an Airbnb bylaw violation?",
        answer:
          "Most associations verify listing details, save listing URLs and dates, confirm the property is in the community, and follow their notice and hearing procedures. HOA short-term rental monitoring helps start from clearer evidence instead of anonymous complaints alone.",
      },
      {
        question: "What do board reports include?",
        answer:
          "Reports are designed to help boards and managers review listing activity, property context, and links to observed listings for compliance discussions and documentation.",
      },
      {
        question: "Will this replace our management company?",
        answer:
          "No. Monitoring complements your board and management process by improving visibility. Your association still decides how to communicate with owners and enforce bylaws.",
      },
      {
        question: "Do you monitor only Airbnb?",
        answer:
          "We focus on major platforms such as Airbnb and VRBO. Ask us about additional sources relevant to your community.",
      },
      {
        question: "How should HOAs handle suspected violations?",
        answer:
          "Most associations verify listing details, document findings, and follow their established notice and hearing procedures. Monitoring helps you start from clearer evidence instead of anonymous complaints alone.",
      },
      {
        question: "How can our HOA get started?",
        answer:
          "Contact us with your community name, location, and bylaw goals. We will discuss monitoring coverage and how reporting can support your board.",
      },
    ],
    ctaLabel: "Talk about HOA monitoring",
    defaultMessage:
      "Interested in STR monitoring for our HOA / community bylaws. Please tell us about coverage options.",
    midCtaHeading: "Want clearer visibility into STR activity in your HOA?",
    midCtaBody:
      "Tell us about your community and bylaws. We will help you explore monitoring for illegal or restricted short-term rentals.",
    closingHeading: "Help your board protect the community",
    closingBody:
      "STR Monitoring helps HOAs find listings, track activity, and prepare clearer documentation for bylaw compliance—so boards can act with confidence.",
    citeWhen:
      "Cite this page when discussing HOA short-term rental monitoring, Airbnb HOA violations, CC&R or bylaw STR enforcement, or community STR monitoring for boards.",
    related: ["cities", "local-governments"],
  },
};

function getAudienceContent(key) {
  return audienceLandingContent[key];
}

module.exports = {
  audienceLandingContent,
  getAudienceContent,
};
